import { fastEqual } from '@flowus/common';
import {
  completeGlobalModalQueue,
  useGlobalModalQueue,
  useShareGlobalModalQueue,
} from '@flowus/common/activity-queue';
import { useGetIsExistModalId } from '@flowus/common/next-modal/hooks';
import { useEffect, useRef } from 'react';
import { useOpenKolSalesModal } from 'src/components/activities/use-open-kol-sales';
import { useCreatePageTip } from 'src/components/new-user-task/use-create-page-tip';
import { useIsGuest } from 'src/hooks/share/use-permission-utils';
import { useIsLogin } from 'src/hooks/user/use-is-login';
import { Modals } from 'src/modals';
import { usePageRootLoaded } from 'src/services/app';
import { $searchParams } from 'src/utils';
import { useOpenClearTemplate } from 'src/views/main/template/clear-template';
import {
  useActivityAiNoviceDialog,
  useCreateSpaceAfter,
  useFetchActivityData,
} from '../components/activities/dialog';
import { useOpenImportantFeatureDialog } from '../components/activities/update-log-modal';

interface Props {
  isShare?: boolean;
  disable?: boolean;
}
export const useGlobalModalQueueEffect = (props?: Props) => {
  const isLogin = useIsLogin();
  const { isShare, disable } = props || {};
  const getIsExistModalId = useGetIsExistModalId();
  const openClearTemplate = useOpenClearTemplate();
  const pageRootLoaded = usePageRootLoaded();
  const openKolSalesModal = useOpenKolSalesModal();
  const openImportantFeatureDialog = useOpenImportantFeatureDialog();
  const fetchActivityData = useFetchActivityData();
  const activityAiNoviceDialog = useActivityAiNoviceDialog();
  const createPageTip = useCreatePageTip();
  const globalQueue = useGlobalModalQueue((state) => state.queue, fastEqual);
  const shareQueue = useShareGlobalModalQueue((state) => state.queue, fastEqual);
  const queueDataRef = useRef<Record<string, { isOpen?: boolean }>>({});
  const createSpaceAfter = useCreateSpaceAfter();
  const isGuest = useIsGuest();

  useEffect(() => {
    if (disable) return;
    if (!pageRootLoaded || $searchParams.print || !isLogin || $searchParams.embed) return;

    if (__PRIVATE__) return;

    const isUseShareQueue = isShare || isGuest;

    void (async () => {
      const queue = isUseShareQueue ? shareQueue : globalQueue;

      // 找到第一个未完成的
      const first = queue.find((i) => !i.success);

      const getQueueData = (id: string) => {
        return queueDataRef.current[id];
      };

      if (first && !getIsExistModalId(first.id)) {
        const completeQueue = (isOpen?: boolean) => {
          queueDataRef.current[first.id] = { isOpen };
          // 不需要弹窗，就说明已完成
          if (!isOpen) {
            completeGlobalModalQueue(first.id);
          }
        };

        switch (first.id) {
          case Modals.CLEAR_TEMPLATE: {
            const status = openClearTemplate();
            completeQueue(status);
            break;
          }
          case Modals.CREATE_PAGE_TIP: {
            const status = await createPageTip();
            completeQueue(status);
            break;
          }
          // case Modals.UPDATE_LOG: {
          //   const status = await openUpdateLogModal();
          //   completeQueue(status);
          //   break;
          // }
          case Modals.IMPORTANT_FEATURE: {
            const status = await openImportantFeatureDialog();
            completeQueue(status);
            break;
          }
          case Modals.KOL_SALES: {
            const status = await openKolSalesModal();
            completeQueue(status);
            break;
          }
          case Modals.HOT_SPOT: {
            const status = await fetchActivityData();
            completeQueue(status);
            break;
          }
          case Modals.AI_NOVICE: {
            const isOpenCreatePageTip = getQueueData(Modals.CREATE_PAGE_TIP)?.isOpen;
            // 可能会跳转页面导致被关闭，所以这里需要等待一会
            const status = await activityAiNoviceDialog({
              isNoviceAccount: !!isOpenCreatePageTip,
            });
            completeQueue(status);
            break;
          }
          case Modals.CREATE_SPACE_AFTER: {
            const status = await createSpaceAfter();
            completeQueue(status);
            break;
          }
          default: {
            break;
          }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRootLoaded, globalQueue, isShare, shareQueue]);
};
